import { Button, cn } from '@landler/component-library';
import { useTranslation } from 'react-i18next';

import { MAP_LAYER_FACTS } from '@/api/rest/resources/types/fact';
import { WidgetSlot } from '@/api/rest/resources/types/widget';
import { Slider } from '@/components/widgets/a/Slider/Slider';
import { useYearsRangeForHabitatIntactness } from '@/hooks/useYearsRangeForHabitatIntactness';
import { Logger } from '@/lib/logs/logger';
import { isNullish } from '@/utils/booleans';

import { useControlsContext, useMapSelectedLayer } from './hooks/useFormControls';

const FALL_BACK_START_YEAR = 1950;
const FALL_BACK_END_YEAR = 2030;

export const YearSlider = () => {
  const { t } = useTranslation();

  const controlsForm = useControlsContext();
  const [selectedYear, inSituLayerConfiguration] = controlsForm.watch(['year', 'inSituLayerConfiguration']);

  const mediaMapWidgets = Array.isArray(inSituLayerConfiguration)
    ? (inSituLayerConfiguration.flatMap((configuration) => configuration?.widget?.widget_format ?? []) as WidgetSlot[])
    : null;

  const selectedLayers = useMapSelectedLayer();
  const hasHabitatIntactnessLayer = selectedLayers.some(
    (layer) => layer.value === MAP_LAYER_FACTS.habitat_intactness_raster_urls,
  );

  const hasTimeSeriesLayer = selectedLayers.some((layer) => layer.isTimeSeries);

  const insituStartYears: string[] = [];
  const insituEndYears: string[] = [];

  if (
    selectedLayers?.some((layer) => layer.value.includes(MAP_LAYER_FACTS.in_situ_map_layer)) &&
    mediaMapWidgets?.length
  ) {
    mediaMapWidgets.forEach((widget) => {
      if (Array.isArray(widget) && widget.length > 1) {
        Logger.warn(`Multiple widgets found for the same configuration: ${JSON.stringify(widget)}`);
        return;
      }

      const formattedWidget = Array.isArray(widget) ? widget[0] : widget;

      const dateFilters = formattedWidget?.relatedData?.dateFilters;
      const inSituStartDate = new Date(dateFilters?.start_date_inclusive).getFullYear();
      const inSituEndDate = new Date(dateFilters?.end_date_inclusive).getFullYear();

      if (!Number.isNaN(inSituStartDate)) {
        insituStartYears.push(inSituStartDate.toString());
      }

      if (!Number.isNaN(inSituEndDate)) {
        insituEndYears.push(inSituEndDate.toString());
      }
    });
  }

  const startYears = selectedLayers
    ?.map((layer) => layer.timeSeriesStart)
    .concat(insituStartYears)
    .filter((startYear) => !isNullish(startYear))
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    .sort((a, b) => new Date(a!).getTime() - new Date(b!).getTime());
  const endYears = selectedLayers
    ?.map((layer) => layer.timeSeriesEnd)
    .concat(insituEndYears)
    .filter((endYear) => !isNullish(endYear))
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    .sort((a, b) => new Date(a!).getTime() - new Date(b!).getTime());

  const startYear =
    startYears && startYears.length > 0 ? new Date(startYears[0] as string).getFullYear() : FALL_BACK_START_YEAR;

  const endYear =
    endYears && endYears.length > 0
      ? new Date(endYears[endYears.length - 1] as string).getFullYear()
      : FALL_BACK_END_YEAR;

  const stepSize = selectedLayers.some((layer) => layer.isTimeSeries && layer.timeSeriesStepSize === 'yearly') ? 1 : 10;

  const handleOnValueChange = (newYear: number) => {
    if (typeof newYear !== 'number') {
      return;
    }
    controlsForm.setValue('year', newYear.toString());
  };

  if (startYear === endYear) {
    return null;
  }

  return (
    <Button
      label={
        selectedYear && hasTimeSeriesLayer
          ? selectedYear
          : t('shared.projects.project.conservation.interactiveMap.labels.year')
      }
      variant='contained'
      className='bg-white-100 hover:bg-white-100 group/time text-text-primary font-bold'
      size='small'
      data-testid='year-slider-trigger'
      disabled={!hasTimeSeriesLayer}
    >
      <div
        className={cn(
          'typography-button2 tracking-[0.3px]',
          'bg-white-100 hover:bg-white-100 hidden group-hover/time:block px-0 py-0',
          !hasTimeSeriesLayer && 'group-hover/time:hidden',
        )}
      >
        {hasHabitatIntactnessLayer ? (
          // It is important to keep a separate component for the HI as this calls the plot reports API. This is only available in the plot pages, where the HI map layer is also only available
          <WithHabitatIntactnessSlider
            startYear={startYear}
            endYear={endYear}
            stepSize={stepSize}
            onChange={handleOnValueChange}
            selectedYear={selectedYear}
          />
        ) : (
          <Slider
            min={startYear}
            max={endYear}
            step={stepSize}
            value={parseInt(selectedYear, 10)}
            onChange={handleOnValueChange}
            showMinMax
            className='px-0 py-0'
          />
        )}
      </div>
    </Button>
  );
};

const WithHabitatIntactnessSlider = ({
  startYear,
  endYear,
  stepSize,
  onChange,
  selectedYear,
}: {
  startYear: number;
  endYear: number;
  stepSize: number;
  onChange: (year: number) => void;
  selectedYear: string;
}) => {
  const yearsRangeForHabitatIntactness = useYearsRangeForHabitatIntactness();

  const HIstartYear = yearsRangeForHabitatIntactness ? yearsRangeForHabitatIntactness[0] : FALL_BACK_START_YEAR;
  const HIendYear = yearsRangeForHabitatIntactness ? yearsRangeForHabitatIntactness[1] : FALL_BACK_END_YEAR;
  const newStartYear = Math.min(startYear, HIstartYear);
  const newEndYear = Math.max(endYear, HIendYear);
  const newStepSize = Math.min(stepSize, 1);

  return (
    <Slider
      min={newStartYear}
      max={newEndYear}
      step={newStepSize}
      value={parseInt(selectedYear, 10)}
      onChange={onChange}
      showMinMax
      className='px-0 py-0'
    />
  );
};
